import React, { useEffect, useState } from "react";
import { EditOutlined } from "@ant-design/icons";
import { message } from "antd";
import "./style.css";
import GenericModalForm from "../../../../Components/Modals/GenericModalForm";
import {
  getWiPayConfig,
  updateWiPayConfig,
} from "../../../../API/Admin/SystemConfiguration/WiPayConfig";

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const DISPLAY_FIELDS = [
  { name: "account_number", label: "Account Number" },
  { name: "api_key", label: "API Key" },
  { name: "country_code", label: "Country Code" },
  { name: "currency", label: "Currency" },
  { name: "environment", label: "Environment" },
  { name: "fee_structure", label: "Fee Structure" },
  { name: "origin", label: "Origin" },
];

const DEFAULT_CONFIG = DISPLAY_FIELDS.reduce(
  (acc, field) => ({ ...acc, [field.name]: "-" }),
  {},
);

const WiPayConfig = ({ setBtnConfigs }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const fetchConfig = async () => {
    try {
      const res = await getWiPayConfig();
      const data = res?.data?.data || res?.data;

      if (Math.floor(res?.status / 100) === 2 && data) {
        setConfig(data);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to load WiPay config."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load WiPay config."),
      );
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Edit Configuration",
        icon: <EditOutlined />,
        onClick: () => setIsEditOpen(true),
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBtnConfigs]);

  const handleSave = async (values) => {
    try {
      const payload = {
        account_number: values.account_number,
        api_key: values.api_key,
        environment: values.environment,
      };

      const res = await updateWiPayConfig(payload);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data || res?.data;
        setConfig((prev) => ({ ...prev, ...(data || payload) }));
        message.success(
          res?.data?.message || "WiPay config updated successfully",
        );
        setIsEditOpen(false);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to update WiPay config."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update WiPay config."),
      );
    }
  };

  return (
    <div className="wipay-config">
      <div className="config-card">
        <div className="config-grid">
          {DISPLAY_FIELDS.map((field) => {
            const value =
              config[field.name] === null ||
              config[field.name] === undefined ||
              config[field.name] === ""
                ? "-"
                : String(config[field.name]);

            return (
              <div className="config-field" key={field.name}>
                <label className="config-label">{field.label}</label>
                <div className="config-value-box" title={value}>
                  <span className="config-value-text">{value}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <GenericModalForm
        open={isEditOpen}
        title="Edit WiPay Configuration"
        subtitle="Update the WiPay merchant account credentials"
        okText="Save Changes"
        cancelText="Cancel"
        fields={[
          {
            name: "account_number",
            label: "Account Number",
            placeholder: "Enter account number",
            type: "input",
            rules: [{ required: true, message: "Account number is required" }],
          },
          {
            name: "api_key",
            label: "API Key",
            placeholder: "Enter API key",
            type: "input",
            rules: [{ required: true, message: "API key is required" }],
          },
          {
            name: "environment",
            label: "Environment",
            placeholder: "Select environment",
            type: "select",
            options: [
              { label: "Sandbox", value: "sandbox" },
              { label: "Live", value: "live" },
            ],
            rules: [{ required: true, message: "Environment is required" }],
          },
        ]}
        initialValues={{
          account_number: config.account_number,
          api_key: config.api_key,
          environment:
            config.environment && config.environment !== "-"
              ? config.environment
              : undefined,
        }}
        onCancel={() => setIsEditOpen(false)}
        onOk={handleSave}
      />
    </div>
  );
};

export default WiPayConfig;
